import { appService } from '@store/App/appService';
import { featureToggleService } from '@store/Configuration/featureToggleService';
import { UserScheduleSettingsApi } from '@schedule/api/UserScheduleSettingsApi';
import { UserServiceApi } from '@schedule/api/UserServiceApi';
import { translationDataService } from '@store/Configuration/translationDataService';
import flatten from 'lodash/flatten';
import compact from 'lodash/compact';
import uniq from 'lodash/uniq';
import uniqBy from 'lodash/uniqBy';
import { addDays, format } from 'date-fns';
import i18next from 'i18next';
import autobind from 'autobind';

import {
    AuthUserData,
    OrganizationStatus,
    OrganizationType,
    ReservationData,
} from 'chaika-frontend-types/frontend-cms';
import type { Optional } from 'chaika-common-types';
import { Locale, SessionStorageKeys } from 'chaika-enums';

import { store } from '@store';
import { queryClient } from '@store/queryClient';
import { invalidateUserProfile } from '@user/queries/userProfileQueries';
import { AuthUser } from '@store/AuthUser/AuthUser';
import { organizationService } from '@organization/service/organizaion/organizationService';
import { loadOffices } from '@services/queries/officeQueries';
import { patientService } from '@patient/services/patient/patientService';
import { AsteriskApi } from '@api/AsteriskApi';
import { AuthApi } from '@api/AuthApi';
import { UserApi } from '@api/UserApi';
import { ReservationApi } from '@api/ReservationApi';
import { ScheduleV2Api } from '@schedule/api/ScheduleV2Api';
import { SCHEDULE_V2_PAGE_SIZE } from '@schedule/queries/scheduleV2Queries';
import { CIVIL_DATE_FORMAT } from '@utils/civilDate';
import { OptionType } from '@components/Select';
import { makeOptionType } from '@utils/makeOptionTypes';
import { serviceService } from '@services/services/serviceService';
import { userService } from '@user/services/userService';
import { userPracticeQueryOptions } from '@user/queries/userPracticeQueries';
import { getMainPracticeDoctorIds } from '@user/utils/getPracticeDoctorIds';
import { collectPracticeClinicIds } from '@utils/collectPracticeClinicIds';
import { TimezoneHelper } from '@utils/TimezoneHelper';
import { PaymentMethodApi } from '@api/PaymentMethodApi';
import { legalEntityBranchService } from '@organization/service/legalEntityBranch/legalEntityBranchService';
import { translate } from '@chaika/i18n/helper';
import { legalEntityService } from '@organization/service/legalEntity/legalEntityService';
import { readPaymentWorkplaceIdsByClinic } from '@store/AuthUser/utils/readPaymentWorkplaceIdsByClinic';

export const defaultOrganizationOption: OptionType<string> = { label: translate('notChosen'), value: 'default' };

// 1 сутки
const CHECK_INTERVAL_MS = 24 * 60 * 60 * 1000;
const TIME_LEFT_RETRY_DELAY_MS = 60 * 1000;
const FUTURE_RESERVATIONS_DAYS = 30;

export class AuthUserService {
    private updateTimeLeftTimeout?: NodeJS.Timeout;
    private userProfileLoaded = false;
    private onLoad = false;

    public async getAuthUserData(): Promise<AuthUserData> {
        return AuthApi.getAuthorizedUser();
    }

    public async loadAuthorizedUser(isSchedulePage?: boolean): Promise<boolean> {
        const authUserLoaded = false;
        if (!this.onLoad) {
            this.onLoad = true;
            const authUser = await AuthApi.getAuthorizedUser();

            if (!!authUser?.id) {
                return this.loadAuthorizedUserData(authUser, isSchedulePage);
            }
            this.onLoad = false;
        }

        return authUserLoaded;
    }

    public async loadAuthorizedUserData(authUser: AuthUserData, isSchedulePage?: boolean): Promise<boolean> {
        let authUserLoaded = false;
        authUserLoaded = true;
        const user = await UserApi.get(authUser.id, {
            locale: store.authUser.selectedLanguage === Locale.Ru ? undefined : store.authUser.selectedLanguage,
        });

        const organizationId = user.additionalData?.selectedOrganizationId ?? undefined;
        const legalEntityId = user.additionalData?.selectedLegalEntityId ?? authUser.legalEntityId ?? undefined;
        const officeId = user.additionalData?.selectedOfficeId ?? undefined;
        const paymentWorkplaceIdsByClinic = readPaymentWorkplaceIdsByClinic(user.additionalData);

        Object.assign(
            store.authUser,
            new AuthUser({
                ...authUser,
                ...user,
                organizationId,
                legalEntityId,
                officeId,
                paymentWorkplaceIdsByClinic,
                selectedLanguage: user.additionalData?.selectedLanguage ?? undefined,
            }),
        );

        const organizationsLoaded = isSchedulePage ? organizationService.loadClinics() : organizationService.load();
        const practiceClinicsLoaded = this.loadPracticeClinicIds();

        await settleLoaders({
            paymentMethods: this.loadPaymentMethods(organizationId),
            featureToggles: featureToggleService.load(),
            servicesConfig: appService.loadServicesConfig(),
            supportConfig: appService.loadSupportConfig(),
            organizations: organizationsLoaded,
            users: !isSchedulePage && userService.load(),
            mainPracticeDoctors: this.loadMainPracticeDoctorIds(),
            legalEntities: !isSchedulePage && legalEntityService.loadClinics(),
            selectedLegalEntity: this.loadSelectedOrganizationLegalEntityId(organizationId, isSchedulePage),
            translations: translationDataService.load(),
            authorizationTimeLeft: this.updateAuthorizationTimeLeft(),
            practiceClinics: practiceClinicsLoaded,
            scheduleSettingsSpecialties: this.loadScheduleSettingsSpecialtyIds(),
            userServices: this.loadUserServiceIds(),
            offices:
                !isSchedulePage &&
                this.loadOfficesByUserOrganizations(
                    store.authUser.hasRootAccess ? organizationsLoaded : practiceClinicsLoaded,
                ),
        });

        this.onLoad = false;

        return authUserLoaded;
    }

    public getTimezone(): string {
        const {
            authUser: { organizationId },
            organizationList,
        } = store;

        const organizationTimezone = organizationId ? organizationList.get(organizationId)?.tzCode : undefined;

        return organizationTimezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
    }

    public isAuthorizeInLegalEntity(legalEntityId: string): boolean {
        const { authUser } = store;
        const selectedLegalEntityId = this.getSelectedOrganizationLegalEntityId(authUser.organizationId);

        return !!selectedLegalEntityId && selectedLegalEntityId === legalEntityId;
    }

    public isSameTimezoneWithOrganization(organizationId: Optional<string>): boolean {
        const { organizationList } = store;

        const organizationTimezone =
            (organizationId ? organizationList.get(organizationId)?.tzCode : undefined) ??
            Intl.DateTimeFormat().resolvedOptions().timeZone;

        return TimezoneHelper.isSameTimeZoneOffset(this.getTimezone(), organizationTimezone);
    }

    public async loadEntryTypes(): Promise<void> {
        const { authUser } = store;
        if (authUser.userEntryTypes.length === 0) {
            const entryTypes = await UserApi.getUserEntryTypes(authUser.id);
            authUser.setEntryTypes(entryTypes);
        }
    }

    public needSelectOrganization(): boolean {
        const { authUser } = store;
        return authUser.isAuthorized && !authUser.organizationId;
    }

    @autobind
    public async updateAuthorizationTimeLeft(): Promise<void> {
        // Если в конфиге таймаут больше 35791 минут, то время в мс уходит за возможный интервал (int32) setTimeout
        // Обходное решение, проверять каждый указанный интервал
        try {
            const timeLeft = await AuthApi.getTimeLeft();
            store.authUser.authorizationTimeLeft = timeLeft;

            if (timeLeft > 0) {
                const checkDelay = Math.min(timeLeft + 5000, CHECK_INTERVAL_MS);

                this.updateTimeLeftTimeout = setTimeout(() => this.updateAuthorizationTimeLeft(), checkDelay);
            }
            // eslint-disable-next-line sonarjs/no-ignored-exceptions
        } catch (e) {
            // A failed check is not an expired session: logging out here would end the session shared by every open tab
            this.updateTimeLeftTimeout = setTimeout(() => this.updateAuthorizationTimeLeft(), TIME_LEFT_RETRY_DELAY_MS);
        }
    }

    public async loadProfile(): Promise<void> {
        if (!this.userProfileLoaded) {
            this.userProfileLoaded = true;
            const userProfile = await UserApi.get(store.authUser.id, {
                locale: store.authUser.selectedLanguage === Locale.Ru ? undefined : store.authUser.selectedLanguage,
            });

            store.authUser.setProfileData(userProfile);
            store.userList.addIfNotExist([userProfile]);
        }
    }

    public async logout(): Promise<void> {
        clearTimeout(this.updateTimeLeftTimeout as any as number);
        await AuthApi.logout();
    }

    public async loadFutureReservations(): Promise<void> {
        const authUser = store.authUser;
        const today = new Date();

        const { schedules } = await ScheduleV2Api.getList({
            userId: authUser.id,
            from: format(today, CIVIL_DATE_FORMAT),
            to: format(addDays(today, FUTURE_RESERVATIONS_DAYS), CIVIL_DATE_FORMAT),
            offHours: true,
            limit: SCHEDULE_V2_PAGE_SIZE,
        });
        const reservations = uniqBy(
            schedules.flatMap(schedule => schedule.reservations),
            ({ id }) => id,
        );

        await this.loadReservationsData(reservations);

        authUser.reservationList.set(reservations);
    }

    public async reloadReservation(reservationId: string): Promise<void> {
        const authUser = store.authUser;
        if (authUser.reservationList.has(reservationId)) {
            const reservation = await ReservationApi.getReservation(reservationId);
            await this.loadReservationsData([reservation]);
            authUser.reservationList.update(reservationId, reservation);
        }
    }

    public async changeReservationPatientCame(reservationId: string, patientCame: boolean): Promise<void> {
        const authUser = store.authUser;
        if (authUser.reservationList.has(reservationId)) {
            await ReservationApi.updatePatientCame(reservationId, patientCame);
            const reservation = authUser.reservationList.getExisting(reservationId);
            reservation.patientCame = patientCame;
            authUser.reservationList.update(reservationId, reservation);
        }
    }

    public async updateUserLang(userLang: Locale): Promise<void> {
        if (userLang !== store.authUser.selectedLanguage) {
            await UserApi.updateAdditionalData(store.authUser.id, {
                selectedLanguage: userLang,
            });
            store.authUser.setSelectedLang(userLang);
            i18next.changeLanguage(userLang);
        }
    }

    public async updateSelectedOrganization(organizationId?: string): Promise<void> {
        if (organizationId) {
            await legalEntityBranchService.loadByOrganizationIds([organizationId]);
        }

        const legalEntityId = this.getSelectedOrganizationLegalEntityId(organizationId);

        store.authUser.legalEntityId = legalEntityId;
        store.authUser.organizationId = organizationId;
        store.authUser.officeId = organizationId
            ? store.authUser.paymentWorkplaceIdsByClinic[organizationId]
            : undefined;

        sessionStorage.setItem(SessionStorageKeys.FromAuthPage, 'false');

        await UserApi.updateAdditionalData(store.authUser.id, {
            selectedOrganizationId: organizationId ?? null,
            selectedOfficeId: store.authUser.officeId ?? null,
            selectedLegalEntityId: legalEntityId ?? null,
        });
        void invalidateUserProfile(queryClient, store.authUser.id);

        store.authUser.paymentMethods = !!organizationId
            ? await PaymentMethodApi.getOptions({
                  organizationId,
                  renderedServiceIds: [],
              })
            : [];
    }

    public async rememberPaymentWorkplace(clinicId: string, workplaceId: string): Promise<void> {
        const { authUser } = store;

        if (authUser.paymentWorkplaceIdsByClinic[clinicId] === workplaceId) {
            return;
        }

        authUser.rememberPaymentWorkplace(clinicId, workplaceId);

        await UserApi.updateAdditionalData(authUser.id, {
            selectedPaymentWorkplaceIds: authUser.paymentWorkplaceIdsByClinic,
            selectedOfficeId: authUser.officeId ?? null,
        });
        void invalidateUserProfile(queryClient, authUser.id);
    }

    public getSelectedOrganizationLegalEntityId(organizationId?: string): Optional<string> {
        const { legalEntityBranchList, authUser } = store;
        const selectedOrganizationId = organizationId ?? authUser.organizationId ?? '';

        return !!selectedOrganizationId
            ? legalEntityBranchList.getActualOnDayLegalEntityId(selectedOrganizationId, new Date())
            : undefined;
    }

    /**
     * Организации, между которыми юзер переключает рабочее место: клиники его практик.
     */
    public getUserOrganizationIds(): string[] {
        const { authUser, organizationList } = store;

        if (authUser.hasRootAccess) {
            return organizationList.getFilteredValuesIds({
                status: OrganizationStatus.Active,
                type: OrganizationType.Clinic,
            });
        }

        return authUser.practiceClinicIds;
    }

    /**
     * Специальности врача живут в настройках приёма.
     */
    public getAuthUserSpecialtyIds(): string[] {
        return store.authUser.scheduleSettingsSpecialtyIds;
    }

    /**
     * Услуги, которые врач оказывает: связь врач↔услуга живёт в user_service.
     */
    public getAuthUserServiceIds(): string[] {
        return store.authUser.userServiceIds;
    }

    public makeOrganizationOptions(): OptionType<string>[] {
        const {
            authUser: { hasRootAccess },
            organizationList,
        } = store;
        const organizations = organizationList
            .getValuesByIds(this.getUserOrganizationIds())
            .sort((a, b) => a.abbreviation.localeCompare(b.abbreviation, ['en', 'ru']));
        const organizationOptions = makeOptionType(
            organizations.map(({ id, abbreviation }) => ({ id, name: abbreviation })),
        );

        if (hasRootAccess) {
            organizationOptions.push(defaultOrganizationOption);
        }

        return organizationOptions;
    }

    public async initSipAccount(): Promise<void> {
        const {
            app,
            authUser,
            authUser: { id, login, cityPhone, organizationId },
            organizationList,
        } = store;

        if (organizationId && authUser.sipAccount?.organizationId !== organizationId) {
            app.isOnUpdate = true;
            const organization = organizationList.get(organizationId);

            const organizationCode = organization?.abbreviation || '';
            const sip = {
                login,
                cityPhone,
                organizationCode,
                password: id,
                type: 'doctor',
            };
            try {
                await AsteriskApi.addWebrtcAccount(sip);
                authUser.sipAccount = {
                    sipuri: `${organizationCode}-${sip.login}@srv-pbx-02.chaika.com`.toLowerCase(),
                    password: sip.password,
                    organizationId,
                };
            } catch (e: any) {
                console.error(translate('accountInitializingError'), e);
            }
            app.isOnUpdate = false;
        }
    }

    public getMainDoctorAndManagerIds(withAuthUserData?: boolean): { mainDoctorIds: string[]; managerIds: string[] } {
        const authUser = store.authUser;

        const mainDoctorIds = authUser.isAssistant ? authUser.mainPracticeDoctorIds : [];

        const managerIds = authUser.isAssistant ? [...mainDoctorIds, ...authUser.selectedUserIds] : [];

        const managerIdsSet = new Set(managerIds);

        if (withAuthUserData) {
            managerIdsSet.add(authUser.id);
        }

        return {
            mainDoctorIds,
            managerIds: Array.from(managerIdsSet.values()),
        };
    }

    private async loadMainPracticeDoctorIds(): Promise<void> {
        const { authUser } = store;

        if (!authUser.isAssistant) {
            return;
        }

        const practices = await queryClient.fetchQuery(userPracticeQueryOptions.byUserId(authUser.id));
        const doctorIds = getMainPracticeDoctorIds({ practices, userId: authUser.id });

        await userService.loadIfNotExist(doctorIds);
        authUser.setMainPracticeDoctorIds(doctorIds);
    }

    private async loadPaymentMethods(organizationId?: string): Promise<void> {
        store.authUser.paymentMethods = !!organizationId
            ? await PaymentMethodApi.getOptions({ renderedServiceIds: [], organizationId })
            : [];
    }

    private async loadSelectedOrganizationLegalEntityId(
        organizationId?: string,
        isSchedulePage?: boolean,
    ): Promise<void> {
        if (!isSchedulePage) {
            await legalEntityBranchService.load();
        } else if (!!organizationId) {
            await legalEntityBranchService.loadByOrganizationIds([organizationId]);
        }

        store.authUser.legalEntityId = this.getSelectedOrganizationLegalEntityId(organizationId);
    }

    private async loadOfficesByUserOrganizations(userOrganizationsLoaded: Promise<void>): Promise<void> {
        await userOrganizationsLoaded;

        const organizationIds = this.getUserOrganizationIds();
        if (organizationIds.length === 0) {
            return;
        }

        const offices = await loadOffices({ organizationId: organizationIds });
        store.officeList.set(offices);
    }

    private async loadPracticeClinicIds(): Promise<void> {
        const practices = await queryClient.fetchQuery(userPracticeQueryOptions.byUserId(store.authUser.id));

        store.authUser.setPracticeClinicIds(collectPracticeClinicIds(practices));
    }

    private async loadUserServiceIds(): Promise<void> {
        const userServices = await UserServiceApi.list({ userId: store.authUser.id });

        store.authUser.setUserServiceIds(uniq(userServices.map(({ serviceId }) => serviceId)));
    }

    private async loadScheduleSettingsSpecialtyIds(): Promise<void> {
        const settings = await UserScheduleSettingsApi.list({ userId: store.authUser.id });

        store.authUser.setScheduleSettingsSpecialtyIds(uniq(settings.flatMap(({ specialties }) => specialties)));
    }

    private async loadReservationsData(reservations: ReservationData[]): Promise<void> {
        const serviceIds: string[] = flatten(reservations.map(({ serviceIds = [] }) => serviceIds));
        const patientIds: string[] = compact(reservations.map(({ patientId }) => patientId));

        await Promise.all([patientService.loadByIds(patientIds), serviceService.loadByIds(serviceIds)]);
    }
}

export const authUserService = new AuthUserService();

async function settleLoaders(loaders: Record<string, Promise<void> | false>): Promise<void> {
    const names = Object.keys(loaders);
    const results = await Promise.allSettled(Object.values(loaders));

    results.forEach((result, index) => {
        if (result.status === 'rejected') {
            console.error(`Auth user loader "${names[index]}" failed`, result.reason);
        }
    });
}
